import React from 'react';
import { Paper, Box, Typography, Chip, Button } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Project } from '../../types';
import { cleanMarkdown, getProjectStateLabel } from '../../utils';

interface ProjectDetailsPanelProps {
    currentProject: Project | undefined;
    onOpenProjectModal: (project: Project) => void;
}

const ProjectDetailsPanel: React.FC<ProjectDetailsPanelProps> = ({
    currentProject,
    onOpenProjectModal
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 3,
                height: 'fit-content',
                minHeight: '400px',
                position: 'sticky',
                top: '20px',
                background: 'rgba(25, 25, 35, 0.8)',
                border: '1px solid rgba(63, 81, 181, 0.2)',
            }}
        >
            {currentProject ? (
                <>
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: 'primary.main' }}>
                                📋 Project
                            </Typography>
                            <Chip
                                label={getProjectStateLabel(currentProject.state)}
                                size="small"
                                sx={{
                                    fontSize: '0.65rem',
                                    height: '20px',
                                    backgroundColor: currentProject.state.toLowerCase() === 'completed' ? 'rgba(76, 175, 80, 0.2)' :
                                        (currentProject.state.toLowerCase() === 'active' ||
                                            currentProject.state.toLowerCase() === 'started' ||
                                            currentProject.state.toLowerCase() === 'in-progress') ? 'rgba(255, 193, 7, 0.2)' :
                                            currentProject.state.toLowerCase() === 'paused' ? 'rgba(255, 152, 0, 0.2)' :
                                                'rgba(255, 255, 255, 0.1)',
                                    color: currentProject.state.toLowerCase() === 'completed' ? '#4caf50' :
                                        (currentProject.state.toLowerCase() === 'active' ||
                                            currentProject.state.toLowerCase() === 'started' ||
                                            currentProject.state.toLowerCase() === 'in-progress') ? '#ffc107' :
                                            currentProject.state.toLowerCase() === 'paused' ? '#ff9800' :
                                                'text.secondary',
                                    border: '1px solid',
                                    borderColor: currentProject.state.toLowerCase() === 'completed' ? '#4caf50' :
                                        (currentProject.state.toLowerCase() === 'active' ||
                                            currentProject.state.toLowerCase() === 'started' ||
                                            currentProject.state.toLowerCase() === 'in-progress') ? '#ffc107' :
                                            currentProject.state.toLowerCase() === 'paused' ? '#ff9800' :
                                                'rgba(255, 255, 255, 0.2)',
                                }}
                            />
                        </Box>
                        <Typography variant="h5" sx={{ fontSize: '1.1rem', fontWeight: 700, mb: 1 }}>
                            {currentProject.name}
                        </Typography>
                        {currentProject.lead && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                Lead: {currentProject.lead.name}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1, color: 'primary.light' }}>
                            Project Overview
                        </Typography>
                        {(currentProject.description || currentProject.content) ? (
                            <Box
                                sx={{
                                    maxHeight: '250px',
                                    overflow: 'auto',
                                    pr: 1,
                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                        color: 'text.primary',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        marginTop: 0.5,
                                        marginBottom: 0.25,
                                    },
                                    '& p': {
                                        color: 'text.primary',
                                        fontSize: '0.75rem',
                                        lineHeight: 1.4,
                                        marginBottom: 0.5,
                                    },
                                    '& ul, & ol': {
                                        color: 'text.primary',
                                        fontSize: '0.75rem',
                                        paddingLeft: 1,
                                    },
                                    '& li': {
                                        marginBottom: 0.125,
                                    },
                                    '& code': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        padding: '1px 2px',
                                        borderRadius: 1,
                                        fontSize: '0.7em',
                                    },
                                    '& blockquote': {
                                        borderLeft: '2px solid rgba(63, 81, 181, 0.5)',
                                        paddingLeft: 1,
                                        marginLeft: 0,
                                        fontStyle: 'italic',
                                        fontSize: '0.75rem',
                                    },
                                    '& a': {
                                        color: 'primary.light',
                                        fontSize: '0.75rem',
                                    },
                                    '&::-webkit-scrollbar': {
                                        width: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '2px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'rgba(63, 81, 181, 0.5)',
                                        borderRadius: '2px',
                                    },
                                }}
                            >
                                <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                                    {cleanMarkdown(currentProject.content || currentProject.description || '')}
                                </ReactMarkdown>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                No description available for this project.
                            </Typography>
                        )}
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onOpenProjectModal(currentProject)}
                        sx={{
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1.5,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                borderColor: 'primary.light',
                                background: 'rgba(63, 81, 181, 0.1)',
                            }
                        }}
                    >
                        View Full Details
                    </Button>
                </>
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        📋 Project Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                        Select a project to view details
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default ProjectDetailsPanel;
